import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Mail, Phone, Download, Filter, Settings, Droplets, Box } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const BasketWithBagPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What is a Basket with Bag (BWB) filter and how does it work?",
      answer: "The BWB combines a rigid metal basket with replaceable filter bag inserts for fine filtration. Water enters the filter chamber, passes through the bag (which captures particles), and exits through the outlet. The basket supports the bag and prevents collapse under pressure. Bags are available in 1-200 micron ratings for targeted filtration—from coarse algae removal to fine silt capture. When the bag becomes clogged, simply open the lid, remove the used bag, insert a new one, and resume operation."
    },
    {
      question: "What micron ratings are available for BWB filter bags?",
      answer: "BWB filter bags are available in a wide micron range: coarse (200, 150, 100 micron) for algae and large organics, medium (50, 25, 10 micron) for silt and suspended solids, fine (5, 1 micron) for micro-particulates and polishing. Select micron rating based on your filtration goal: coarse bags handle higher flow with less frequent changes, fine bags provide superior filtration but require more frequent replacement. Our filtration specialists can recommend optimal micron rating for your water quality and application."
    },
    {
      question: "How often do BWB filter bags need replacement?",
      answer: "Bag replacement frequency depends on water quality, debris loading, and micron rating. Clean water with fine bags: weekly to monthly. Moderate debris with medium bags: daily to weekly. Heavy organic matter with coarse bags: multiple times per day. Monitor differential pressure—replace bag when ΔP exceeds 10-15 PSI or flow rate drops noticeably. Most installations keep spare bags on hand for quick changeovers. Bags are disposable (typical) or washable/reusable (specialty fabrics)."
    },
    {
      question: "What materials are BWB filters available in?",
      answer: "BWB filters are available in mild steel with epoxy coating (economical, suitable for most agricultural applications) or 304/316 stainless steel (corrosive environments, food-grade, marine). Filter bags are typically polypropylene felt or nylon mesh—both food-safe and chemically compatible with most irrigation and process water. Seals and gaskets: EPDM (standard) or Viton (chemical resistance, high temperature). Material selection depends on water chemistry, temperature, and application requirements."
    },
    {
      question: "Can BWB filters be used with chemically treated or process water?",
      answer: "Yes, with appropriate material selection. Standard polypropylene bags handle most irrigation water, chlorinated water, and mild chemicals. For aggressive chemicals, acids, or solvents, specify chemical-resistant bags (PTFE, special nylon) and Viton seals. Stainless steel construction recommended for corrosive environments. Always verify chemical compatibility before use. Our filtration specialists can review your water chemistry and recommend compatible materials for bags, seals, and filter body."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Basket with Bag (BWB) Filters | Fine Filtration | CTC Waterworks"
        description="BWB Basket with Bag specialty filters for fine algae, silt, and micro-particulate control. Replaceable bag inserts with 1-200 micron range. Complete specifications and replacement parts."
        canonical="https://www.ctcwaterworks.com/filtration/water-intake/basket-with-bag"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/water-intake" className="text-gray-600 hover:text-ctc-blue">Water Box & Specialty</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Basket with Bag</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Filter className="text-orange-600 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      Basket with Bag (BWB) Filters
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Fine filtration for algae, silt, and micro-particulate control.
                </p>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">About BWB Filters</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The BWB (Basket with Bag Filter) is a specialty filtration unit combining a rigid metal basket and replaceable bag insert. It provides fine filtration beyond screen intake capabilities and is ideal for algae, silt, and micro-particulate control in irrigation and process water applications.
                  </p>
                </div>

                <div className="flex justify-center mt-6">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{maxWidth: '500px'}}>
                    <img src="/BWB.jpeg" alt="BWB Filter - Complete Assembly" className="w-full h-auto" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-orange-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>BWB Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-orange-600 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose BWB Filters?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Filter className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Interchangeable Bags</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Filter bags available in 1-200 micron range for targeted filtration. Change micron rating anytime to match water quality and application needs.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Box className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Removable Basket</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Rigid basket supports bag and prevents collapse under pressure. Quick removal for bag replacement—no tools required, minimal downtime.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Settings className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Fast Access</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Flanged top lid with swing-bolt clamps for fast access. Open, replace bag, close—typical service time 5-10 minutes.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6">
              <Droplets className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Fine Polishing</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Provides filtration beyond coarse intake screens. Ideal for algae, silt, and micro-particulates that escape primary filtration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features List */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Features</h2>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <ul className="space-y-4">
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Interchangeable filter bags (typical 1–200 micron range)</span>
                  <p className="text-gray-700 text-sm mt-1">Select micron rating based on your specific filtration needs—from coarse algae removal to fine silt capture.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Removable basket for bag support and easy maintenance</span>
                  <p className="text-gray-700 text-sm mt-1">Rigid metal basket prevents bag collapse under pressure and lifts out for quick bag replacement.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Flanged top lid with swing-bolt clamps for fast access</span>
                  <p className="text-gray-700 text-sm mt-1">No tools required—open clamps, lift lid, replace bag. Typical service time 5-10 minutes.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Optional pressure gauge ports and drain valve</span>
                  <p className="text-gray-700 text-sm mt-1">Monitor differential pressure to determine optimal bag replacement timing. Drain valve for easy servicing.</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Common inlet/outlet orientation for inline retrofits</span>
                  <p className="text-gray-700 text-sm mt-1">Standard piping configurations make BWB filters easy to install in existing systems.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-6 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Construction:</span> Mild steel with epoxy coating or 304/316 stainless steel options. Filter bags: polypropylene felt or nylon mesh (food-safe, chemically compatible). Seals: EPDM or Viton. Maximum operating pressure: 150 PSI (typical). Contact engineering for material selection guidance based on your water chemistry and application.
            </p>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">BWB Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Specifications represent typical configurations. Contact engineering for detailed specifications based on your flow and application requirements.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Model</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow Range (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">Basket Dia (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Basket Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Max Pressure (psi)</th>
                  <th className="px-4 py-3 text-left font-semibold">Bag Micron Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">BWB-04</td>
                  <td className="px-4 py-3">4" FLG</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">150</td>
                  <td className="px-4 py-3">1–200</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">BWB-06</td>
                  <td className="px-4 py-3">6" FLG</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">150</td>
                  <td className="px-4 py-3">1–200</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">BWB-08</td>
                  <td className="px-4 py-3">8" FLG</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">150</td>
                  <td className="px-4 py-3">1–200</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Note:</span> Flow ranges vary significantly based on micron rating, water quality, and acceptable pressure drop. Coarse bags (100-200 micron) handle higher flows, fine bags (1-10 micron) require lower velocities. Contact engineering for application-specific flow calculations and bag selection recommendations based on your water analysis.
            </p>
          </div>
        </div>
      </section>

      {/* Bag Replacement Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BWB 2.jpeg" alt="BWB Bag Replacement Step" className="w-full h-full object-cover" style={{aspectRatio: '1/1'}} />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BWB 3.jpeg" alt="BWB Bag Replacement Step" className="w-full h-full object-cover" style={{aspectRatio: '1/1'}} />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BWB 4.jpeg" alt="BWB Bag Replacement Step" className="w-full h-full object-cover" style={{aspectRatio: '1/1'}} />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BWB 5.jpeg" alt="BWB Bag Replacement Step" className="w-full h-full object-cover" style={{aspectRatio: '1/1'}} />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/BWB 6.jpeg" alt="BWB Bag Replacement Step" className="w-full h-full object-cover" style={{aspectRatio: '1/1'}} />
            </div>
          </div>
        </div>
      </section>

      {/* Replacement Parts */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Replacement Parts</h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part</th>
                  <th className="px-4 py-3 text-left font-semibold">Description</th>
                  <th className="px-4 py-3 text-left font-semibold">Material</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">Basket</td>
                  <td className="px-4 py-3">Support basket assembly</td>
                  <td className="px-4 py-3">Epoxy-coated steel / SS</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">Bag</td>
                  <td className="px-4 py-3">Filter bag</td>
                  <td className="px-4 py-3">Poly felt / nylon mesh</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">Seal Kit</td>
                  <td className="px-4 py-3">O-rings & gaskets</td>
                  <td className="px-4 py-3">EPDM / Viton</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Filter Bags:</span> Available in multiple micron ratings (1, 5, 10, 25, 50, 100, 150, 200) and materials (polypropylene felt, nylon mesh, specialty fabrics). Stock standard micron ratings for your application. Bags are typically disposable but some specialty fabrics are washable and reusable. Contact your dealer or CTC filtration specialists for bag availability, pricing, and recommended stocking quantities.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Callout */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Water Box vs BWB</h2>

          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Box className="mr-3 text-ctc-blue" size={28} />
                  Water Box
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Large surface area</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Coarse intake screening</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Float valve level control</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Best for bulk debris</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Filter className="mr-3 text-orange-600" size={28} />
                  BWB (Basket with Bag)
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Fine bag filtration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">1-200 micron polishing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Replaceable bag inserts</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">Best for algae/silt/micro-particles</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-blue-200">
              <p className="text-gray-700 leading-relaxed text-center">
                <span className="font-semibold text-gray-900">Use Together:</span> Water Box upstream for coarse screening, BWB downstream for fine polishing—complete staged filtration from intake to emitters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Reference */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Reference</h2>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                  <th className="px-4 py-3 text-left font-semibold">Open Area (%)</th>
                  <th className="px-4 py-3 text-left font-semibold">Equivalent Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Typical Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">0.020–0.025</td>
                  <td className="px-4 py-3">Intake</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.015–0.020</td>
                  <td className="px-4 py-3">Micro</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">80</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.010–0.012</td>
                  <td className="px-4 py-3">Drip</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.008–0.010</td>
                  <td className="px-4 py-3">Fine protection</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Complete Your Filtration System</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/water-intake/water-box"
              className="bg-white border-2 border-ctc-blue rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compare Water Box</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                For coarse intake screening with large screen area, consider Water Box filters. Float-valved level control for consistent flow from ponds and canals.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                View Water Box Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Add BIG inline filters for pressure filtration. BWB handles fine polishing, BIG provides automatic cleaning and modular capacity.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View BIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/tig"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with TIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compact true-inline TIG filters for space-constrained installations. Works downstream of BWB for additional polishing stages.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                View TIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-orange-600 transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Add Fine Filtration with BWB?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Our filtration specialist team will help you select the right BWB model, recommend optimal bag micron ratings, and design a complete staged filtration system for your water quality and flow requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-orange-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My System
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-orange-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BasketWithBagPage;
